import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { CustomerCollectionRecord, ProjectCollectionRecord } from '@shared/types';
import { dispatchShellDomainInvalidated } from '@renderer/app/events/shell-domain-events';
import {
  QuickAddFields,
  createQuickAddContext,
  getQuickAddDomainLabel,
  submitQuickAddDomain,
  type QuickAddOption
} from '@renderer/app/quick-add/quick-add-shared';
import { useQuickAddStore, type QuickAddDomain } from '@renderer/app/state/quick-add-store';
import { useTasksViewStore } from '@renderer/domains/tasks/tasks-view-store';
import { Icon } from '@renderer/shared/components/icons';
import { SelectField } from '@renderer/shared/components/primitives';
import { useI18n } from '@renderer/shared/i18n';
import { listCustomers } from '@renderer/domains/customers/customers-repository';
import { listProjects } from '@renderer/domains/projects/projects-repository';

export function QuickAddModal(): JSX.Element | null {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const {
    activeDomain,
    close,
    draftsByDomain,
    error,
    isOpen,
    resetDraft,
    setActiveDomain,
    setError,
    setStatus,
    status,
    updateDraft
  } = useQuickAddStore();
  const setHighlightTaskId = useTasksViewStore((state) => state.setHighlightTaskId);
  const [customers, setCustomers] = useState<CustomerCollectionRecord | null>(null);
  const [projects, setProjects] = useState<ProjectCollectionRecord | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    titleRef.current?.focus();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setError(null);
    void Promise.all([listCustomers(), listProjects()])
      .then(([customerCollection, projectCollection]) => {
        setCustomers(customerCollection);
        setProjects(projectCollection);
      })
      .catch((nextError) => {
        setError(nextError instanceof Error ? nextError.message : 'Quick add unavailable.');
      });
  }, [isOpen, setError]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }

      if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        void submit();
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, activeDomain, draftsByDomain]);

  const domainOptions = useMemo<QuickAddOption[]>(
    () => [
      { value: 'projects', label: t('navigation.projects') },
      { value: 'leads', label: t('navigation.leads') },
      { value: 'tasks', label: t('navigation.tasks') },
      { value: 'customers', label: t('navigation.customers') },
      { value: 'resources', label: t('navigation.resources') },
      { value: 'contracts', label: t('navigation.contracts') },
      { value: 'invoices', label: t('navigation.invoices') }
    ],
    [t]
  );
  const quickAddContext = useMemo(
    () =>
      createQuickAddContext({
        customers: customers?.customers ?? [],
        projects: projects?.projects ?? [],
        customerCategories: customers?.customerCategories ?? [],
        t
      }),
    [customers, projects, t]
  );

  if (!isOpen) {
    return null;
  }

  const isBusy = status === 'submitting';
  const domainLabel = getQuickAddDomainLabel(activeDomain, t);

  async function submit(): Promise<void> {
    setError(null);
    setStatus('submitting');

    try {
      const result = await submitQuickAddDomain(activeDomain, draftsByDomain[activeDomain], t);
      resetDraft(activeDomain);
      close();
      dispatchShellDomainInvalidated(result.domain);

      if (result.domain === 'tasks') {
        if (location.pathname !== '/tasks') {
          void navigate('/tasks');
        }
        setHighlightTaskId(result.entityId);
        return;
      }

      void navigate(result.route);
    } catch (nextError) {
      setStatus('error');
      setError(nextError instanceof Error ? nextError.message : 'Create failed.');
    }
  }

  return (
    <div
      className="av-quick-add-modal__overlay"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          close();
        }
      }}
    >
      <div
        aria-labelledby="quick-add-title"
        aria-modal="true"
        className="av-quick-add-modal"
        role="dialog"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="av-quick-add-modal__header">
          <h2 id="quick-add-title" ref={titleRef} tabIndex={-1}>
            {t('quickAdd.title', { domain: domainLabel })}
          </h2>
          <button className="av-quick-add-modal__close" type="button" onClick={close}>
            <Icon name="times" />
          </button>
        </div>

        <div className="av-quick-add-modal__body">
          <div className="av-quick-add-modal__row">
            <span className="av-field-label">{t('quickAdd.domain')}</span>
            <SelectField
              value={activeDomain}
              options={domainOptions}
              onChange={(event) => setActiveDomain(event.target.value as QuickAddDomain)}
            />
          </div>

          {error ? <div className="av-inline-error">{error}</div> : null}

          <div className="av-quick-add-modal__form">
            <QuickAddFields
              context={quickAddContext}
              domain={activeDomain}
              draft={draftsByDomain[activeDomain]}
              updateDraft={updateDraft}
              variant="modal"
            />
          </div>
        </div>

        <div className="av-quick-add-modal__footer">
          <div className="av-inline-metadata">
            <span>{t('quickAdd.shortcutHint')}</span>
          </div>
          <div className="av-quick-add-modal__actions">
            <button className="av-secondary-button" type="button" onClick={close} disabled={isBusy}>
              {t('common.cancel')}
            </button>
            <button className="av-primary-button" type="button" onClick={() => void submit()} disabled={isBusy}>
              <Icon name="plus" />
              <span>{t('shell.create')}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
